<div class="flex flex-col gap-8">
  <pr-information-box>
    <p>
      Apps can integrate with the Principle Dental API using OAuth 2.0. This
      allows apps to authenticate users and access their data securely. Webhooks
      are also available to notify apps of changes in user data.
      <a
        target="_blank"
        class="inline-flex items-center gap-2"
        [href]="apiDocsUrl"
      >
        <span>View API Docs</span>
        <mat-icon class="icon-sm">open_in_new</mat-icon>
      </a>
    </p>

    <p>
      The OAuth 2.0 flow is an industry-standard protocol for authorisation. We
      use the OAuth 2.0 Authorization Code Flow.
      <a
        target="_blank"
        class="inline-flex items-center gap-2"
        href="https://frontegg.com/blog/oauth-flows"
      >
        <span>Learn More about OAuth 2.0</span>
        <mat-icon class="icon-sm">open_in_new</mat-icon>
      </a>
    </p>
  </pr-information-box>

  <mat-card>
    <mat-card-header class="flex justify-between">
      <mat-card-title>OAuth</mat-card-title>
      <mat-card-subtitle>
        These details will be displayed to the user in the Principle Dental
        OAuth 2.0 flow.
        <a target="_blank" class="flex items-center gap-2" [href]="apiDocsUrl">
          <span>View API Docs</span>
          <mat-icon class="icon-sm">open_in_new</mat-icon>
        </a>
      </mat-card-subtitle>
    </mat-card-header>

    <mat-card-content>
      <form [formGroup]="form" (ngSubmit)="submit()">
        <div class="mt-4 flex flex-col gap-4">
          <div class="mb-4">
            <h3>App Icon</h3>
            <div
              class="group/pic relative h-14 w-14 cursor-pointer overflow-hidden rounded-full bg-slate-200"
            >
              @if (appIconUrl$ | async; as icon) {
                <pt-user-icon
                  class="group-hover/pic:blur-sm"
                  [src]="appIconUrl$ | async"
                  [diameter]="56"
                />
              } @else {
                <div class="flex h-full w-full items-center justify-center">
                  <mat-icon class="text-slate-400"
                    >add_photo_alternate</mat-icon
                  >
                </div>
              }

              <div
                class="/50 absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center bg-slate-50 opacity-0 group-hover/pic:opacity-30"
                [matMenuTriggerFor]="pictureMenu"
              >
                <mat-icon>more_vert</mat-icon>
              </div>
            </div>

            @if ((appIconUrl$ | async) === undefined) {
              <mat-hint class="mat-caption opacity-60">
                Upload an image to use as the app icon.
              </mat-hint>
            } @else {
              <mat-hint class="mat-caption opacity-60">
                Click the icon to change it.
              </mat-hint>
            }

            <mat-menu #pictureMenu="matMenu">
              <pt-image-upload
                [menuButton]="true"
                [uploadPath]="storagePath$ | async"
                (imageUpload)="updateImageUrl($event)"
              />
            </mat-menu>
          </div>

          <mat-form-field class="max-w-lg flex-1">
            <mat-label
              >Redirect URL eg. https://example.com/oauth/callback</mat-label
            >
            <input matInput formControlName="redirectUri" required />
          </mat-form-field>

          <mat-form-field class="max-w-lg flex-1">
            <mat-label>API Permissions</mat-label>
            <mat-select multiple formControlName="scopes">
              @for (scope of apiScopes; track scope) {
                <mat-option [value]="scope">
                  {{ scope }}
                </mat-option>
              }
            </mat-select>
            <mat-icon
              class="help-icon"
              matIconSuffix
              matTooltip="Apps should only request the minimum scopes they need to function"
              >help_outline</mat-icon
            >
          </mat-form-field>

          <div class="flex flex-col justify-start">
            <mat-label class="mb-2">Consent URL</mat-label>
            <div
              class="flex flex-1 items-center justify-between gap-2 rounded-md bg-slate-200 px-4 py-2"
            >
              <span [style.overflow-wrap]="'anywhere'">
                {{ consentUrl() }}
              </span>
              <button
                matIconSuffix
                mat-icon-button
                matTooltip="Copy to clipboard"
                class="mt-1"
                [cdkCopyToClipboard]="consentUrl()"
                (click)="snackBar.open('Copied to clipboard')"
              >
                <mat-icon>content_copy</mat-icon>
              </button>
            </div>
            <mat-hint class="mat-caption opacity-60">
              This is the URL that you will forward the user to so that they can
              authorise your app to access their data in Principle.
              <a target="_blank" href="{{ apiDocsUrl + '/#tag/oauth' }}"
                >More info</a
              >
              on the OAuth 2.0 flow.
            </mat-hint>
          </div>
        </div>

        <pt-buttons-container padding="none">
          <button
            mat-flat-button
            color="primary"
            [disabled]="(canSave$ | async) === false"
          >
            Save
          </button>
        </pt-buttons-container>
      </form>
    </mat-card-content>
  </mat-card>

  <mat-card>
    <mat-card-header>
      <mat-card-title>Credentials</mat-card-title>
      <mat-card-subtitle>
        This is the client ID and secret that you will use to authenticate with
        the Principle Dental API via OAuth 2.0 flows.
      </mat-card-subtitle>
    </mat-card-header>
    <mat-card-content>
      <div class="mt-4 flex flex-col gap-4">
        <div class="flex flex-col justify-start">
          <mat-label class="mb-2">Client ID</mat-label>
          <div
            class="flex max-w-lg flex-1 items-center justify-between rounded-md bg-slate-200 px-4 py-2"
          >
            <span>
              {{ form.controls.clientId.value }}
            </span>
            <button
              matIconSuffix
              mat-icon-button
              matTooltip="Copy to clipboard"
              class="mt-1"
              [cdkCopyToClipboard]="form.controls.clientId.value"
              (click)="snackBar.open('Copied to clipboard')"
            >
              <mat-icon>content_copy</mat-icon>
            </button>
          </div>
          <mat-hint class="mat-caption opacity-60">
            This is the Client ID that you will use to authenticate with the
            Principle Dental API.
          </mat-hint>
        </div>

        <div class="flex flex-col items-start">
          <mat-label class="mb-2">Client Secret</mat-label>
          <button mat-flat-button color="primary" (click)="regenerateSecret()">
            <mat-icon>refresh</mat-icon>
            Generate New Secret
          </button>
          <mat-hint class="mat-caption opacity-60"
            >This does not revoke existing OAuth tokens.</mat-hint
          >
        </div>
      </div>
    </mat-card-content>
  </mat-card>
</div>
